import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { type CarDetail as CarDetailType, type DvlaEventItem, type EventItem, type PhotoItem, getCar } from "../api/client";
import { heroSrc } from "../api/photo";
import FavouriteStar from "../components/FavouriteStar";
import PriceBreakdown from "../components/PriceBreakdown";
import { NEW_ARRIVAL_DAYS, isNewArrival } from "./inventoryColumns";

function fmt(date: string) {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

function fmtGbp(v: unknown): string {
  if (v == null) return "—";
  const val = (v as { value?: number }).value;
  if (val == null) return "—";
  return `£${val.toLocaleString("en-GB")}`;
}

function fmtMiles(v: unknown): string {
  if (v == null) return "—";
  const val = (v as { value?: number }).value;
  if (val == null) return "—";
  return `${val.toLocaleString("en-GB")} mi`;
}

function fmtPartner(v: unknown): string {
  if (v == null) return "—";
  const obj = v as { name?: string | null; city?: string | null };
  const parts = [obj.name, obj.city].filter(Boolean);
  return parts.length ? parts.join(" — ") : "—";
}

function fmtSoh(v: unknown): string {
  if (v == null) return "—";
  return `${(v as number).toFixed(1)}%`;
}

function fmtPlain(v: unknown): string {
  if (v == null) return "—";
  return String(v);
}

function humaniseField(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Friendly host label for a source URL, e.g. "polestar.com" from a full
 *  manufacturer product URL. Falls back to the raw string if it won't parse. */
function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function eventRow(e: EventItem): { field: string; old: string; new: string; statusOnly: boolean } {
  switch (e.event_type) {
    case "price_change":
      return { field: "Price", old: fmtGbp(e.old_value), new: fmtGbp(e.new_value), statusOnly: false };
    case "mileage_change":
      return { field: "Mileage", old: fmtMiles(e.old_value), new: fmtMiles(e.new_value), statusOnly: false };
    case "partner_change":
      return { field: "Partner", old: fmtPartner(e.old_value), new: fmtPartner(e.new_value), statusOnly: false };
    case "handover_change":
      return { field: "Handover", old: fmtPartner(e.old_value), new: fmtPartner(e.new_value), statusOnly: false };
    case "battery_soh_change":
      return { field: "Battery SoH", old: fmtSoh(e.old_value), new: fmtSoh(e.new_value), statusOnly: false };
    case "field_change":
      return {
        field: humaniseField(e.field_name ?? ""),
        old: fmtPlain(e.old_value),
        new: fmtPlain(e.new_value),
        statusOnly: false,
      };
    default:
      return { field: e.event_type.replace(/_/g, " "), old: "", new: "", statusOnly: true };
  }
}

function fmtDvlaDate(value: unknown): string {
  if (typeof value !== "string") return "";
  // DVLA prior/new values store dates as ISO strings; render the day in London.
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function dvlaEventRow(e: DvlaEventItem): { label: string; old: string; new: string } {
  const prior = (e.prior_value ?? {}) as Record<string, unknown>;
  const next = (e.new_value ?? {}) as Record<string, unknown>;
  switch (e.event_type) {
    case "tax_renewed":
      return {
        label: "Tax renewed",
        old: prior.tax_due_at ? `due ${fmtDvlaDate(prior.tax_due_at)}` : "no tax",
        new: next.tax_due_at ? `due ${fmtDvlaDate(next.tax_due_at)}` : "—",
      };
    case "marked_for_export":
      return { label: "Marked for export", old: "no", new: "yes" };
    default:
      return { label: e.event_type.replace(/_/g, " "), old: "", new: "" };
  }
}

function DvlaEventTable({ events }: { events: DvlaEventItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-xs font-semibold text-gray-500 uppercase border-b">
            <th className="text-left px-2 py-1 w-28">Date</th>
            <th className="text-left px-2 py-1">Event</th>
            <th className="text-left px-2 py-1">Old</th>
            <th className="text-left px-2 py-1">New</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const row = dvlaEventRow(e);
            return (
              <tr key={e.id} className="border-b last:border-0">
                <td className="px-2 py-1 text-gray-400 whitespace-nowrap">{fmt(e.occurred_at)}</td>
                <td className="px-2 py-1 capitalize font-medium">{row.label}</td>
                <td className="px-2 py-1 text-gray-500">{row.old}</td>
                <td className="px-2 py-1">{row.new}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EventTable({ events }: { events: EventItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-xs font-semibold text-gray-500 uppercase border-b">
            <th className="text-left px-2 py-1 w-28">Date</th>
            <th className="text-left px-2 py-1">Field</th>
            <th className="text-left px-2 py-1">Old</th>
            <th className="text-left px-2 py-1">New</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const row = eventRow(e);
            return (
              <tr key={e.id} className="border-b last:border-0">
                <td className="px-2 py-1 text-gray-400 whitespace-nowrap">{fmt(e.observed_at)}</td>
                <td className="px-2 py-1 capitalize font-medium">{row.field}</td>
                {row.statusOnly ? (
                  <td className="px-2 py-1 text-gray-400" colSpan={2} />
                ) : (
                  <>
                    <td className="px-2 py-1 text-gray-500">{row.old}</td>
                    <td className="px-2 py-1">{row.new}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GalleryImg({ photo, alt, className }: { photo: PhotoItem; alt: string; className: string }) {
  // Local content-hashed copy first (only the hero has a sha256), upstream
  // CDN URL as the fallback per spec §2.8 + §16.2.
  const primary = heroSrc(photo.url, photo.sha256);
  const [src, setSrc] = useState<string | null>(primary);

  const onError = () => {
    if (src && src !== photo.url) {
      setSrc(photo.url);
    } else {
      setSrc(null);
    }
  };

  if (!src) return <div className={`${className} bg-gray-100`} aria-hidden="true" />;
  return <img src={src} alt={alt} loading="lazy" onError={onError} className={className} />;
}

function PhotoGallery({ photos }: { photos: PhotoItem[] }) {
  const [idx, setIdx] = useState(0);
  if (photos.length === 0) {
    return <p className="text-gray-400 text-sm">No photos available.</p>;
  }
  const current = photos[idx];
  if (!current) return null;
  return (
    <div>
      <GalleryImg
        key={current.url}
        photo={current}
        alt="Car photo"
        className="w-full max-h-96 object-contain bg-gray-100 rounded"
      />
      {photos.length > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button
              key={p.url}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-16 h-12 border-2 rounded overflow-hidden ${
                i === idx ? "border-blue-500" : "border-transparent"
              }`}
            >
              <GalleryImg photo={p} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SpecRow({
  label,
  value,
  canonicalLabels,
}: {
  label: string;
  value: React.ReactNode;
  /** M9.A — canonical ontology labels for this axis, rendered as a small
   *  subtitle when at least one differs from the raw value. Lets the user
   *  see "Charcoal Premium Leather — Premium leather · Black" (raw +
   *  generic) without losing the brand-native vocabulary. */
  canonicalLabels?: string[];
}) {
  if (value === null || value === undefined) return null;
  const subtitle =
    canonicalLabels && canonicalLabels.length > 0
      ? canonicalLabels.filter((l) => l !== value).join(" · ")
      : "";
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium">
        {value}
        {subtitle && (
          <span className="block text-xs italic text-gray-500 font-normal">
            {subtitle}
          </span>
        )}
      </dd>
    </>
  );
}

// Manufacture block: the derived view of when the car was actually built, how
// long it sat before registration, and where it fell in the production run.
//
// All of it is computed server-side in `app/util/manufacture.py` from columns
// we already store — nothing here is persisted, and there is no migration
// behind it. Full derivation and evidence:
// `analysis/vin-decode-ps3-ps4-2026-08.md`.
function ManufactureSection({ car }: { car: CarDetailType }) {
  // `structure_week` is Polestar-only. Without a build date the whole section
  // is empty, so drop it rather than showing a heading over nothing.
  if (!car.structure_week_start) return null;

  const offsetDays = car.registration_offset_days;
  // Both ends of the offset inherit the build week's ±1-week slack, so the day
  // count is stated as approximate rather than exact.
  const offsetLabel =
    offsetDays == null
      ? null
      : `${offsetDays < 0 ? "−" : ""}${Math.abs(offsetDays / 30.44).toFixed(1)} months (~${offsetDays.toLocaleString("en-GB")} days, ±1 wk)`;

  const buildAge = car.age_from_structure_week_years;
  const regAge = car.age_from_registration_years;
  // The gap is the point of the section: what the V5C hides.
  const hidden =
    buildAge != null && regAge != null ? +(buildAge - regAge).toFixed(2) : null;

  return (
    <section className="mt-6">
      <h2 className="text-lg font-semibold mb-2">Structure week</h2>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {/* `structure_week` has WEEK resolution only — the Monday is an anchor,
            not a known day. Label it "w/c" so the UI can't be read as claiming
            a build date we don't have. */}
        <SpecRow
          label="Structure week"
          value={`${car.structure_week} (w/c ${fmt(car.structure_week_start)})`}
        />
        <SpecRow
          label="First registered"
          value={car.first_registered_at ? fmt(car.first_registered_at) : null}
        />
        <SpecRow label="Offset" value={offsetLabel} />
        <SpecRow
          label="Age from structure week"
          value={buildAge != null ? `${buildAge.toFixed(2)} yr` : null}
        />
        <SpecRow
          label="Age from registration"
          value={regAge != null ? `${regAge.toFixed(2)} yr` : null}
        />
        <SpecRow label="VIN plant code" value={car.vin_plant_code} />
        <SpecRow
          label="VIN production no."
          value={
            car.vin_production_serial != null
              ? car.vin_production_serial.toLocaleString("en-GB")
              : null
          }
        />
      </dl>
      {hidden != null && hidden > 0 && (
        <p className="mt-3 text-sm text-gray-600 bg-gray-50 border rounded p-3">
          Structure week is <strong>{hidden.toFixed(2)} years earlier</strong> than
          this car&rsquo;s registration date &mdash; {car.structure_week} (w/c{" "}
          {fmt(car.structure_week_start)}) vs{" "}
          {car.first_registered_at ? fmt(car.first_registered_at) : "later"}.
          <span className="block mt-1 text-xs text-gray-500">
            <code>structureWeek</code> is Polestar&rsquo;s own field, to one-week
            resolution, with no independent source to check it against. It tracks
            the production sequence &mdash; the two-week gaps in it move with
            Chinese New Year &mdash; but whether it marks line-off, body-in-white
            or spec-freeze is not established.
          </span>
        </p>
      )}
    </section>
  );
}

export default function CarDetail() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const [car, setCar] = useState<CarDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCar(id)
      .then((data) => {
        if (!cancelled) setCar(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <div className="p-6 text-gray-500">Loading…</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-red-600 bg-red-50 border border-red-200 rounded m-6">
        Error: {error}
      </div>
    );
  }
  if (!car) {
    return <div className="p-6 text-gray-400">Car not found.</div>;
  }

  const disappeared = car.status === "disappeared";
  const sold = car.sale_status === "sold";
  const exported = car.sale_status === "exported";
  // uuid_reassigned is lineage for the scraper (Polestar re-issuing an ad id),
  // not a change a reader cares about — some cars carry dozens. The API keeps it.
  const historyEvents = car.events.filter((e) => e.event_type !== "uuid_reassigned");

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Carries the inventory's `?…` filter back with it. The filter lives
          only in the query string, so linking to a bare `/{brand}/cars` threw
          away every selection the user had made; `inventoryColumns.tsx` passes
          the search string in on the way here. */}
      <Link
        to={{ pathname: `/${car.brand_code}/cars`, search }}
        className="text-blue-600 hover:underline text-sm"
      >
        ← Back to inventory
      </Link>

      {/* Header */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FavouriteStar carId={car.car_id} initial={car.is_favourite} />
            <h1 className="text-2xl font-bold">{car.display_name ?? car.model_code ?? "Car"}</h1>
          </div>
          {isNewArrival(car.first_seen_at) && (
            <span className="inline-block mt-1 mr-1 text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
              New in the last {NEW_ARRIVAL_DAYS} days
            </span>
          )}
          {sold && (
            <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
              Sold
            </span>
          )}
          {exported && (
            <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
              Exported
            </span>
          )}
          {disappeared && !sold && !exported && (
            <span className="inline-block mt-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
              No longer listed
            </span>
          )}
          {car.motor_label && (
            <p className="mt-1 text-gray-600 text-sm">{car.motor_label}</p>
          )}
          {car.drivetrain && (
            <p className="text-gray-500 text-sm">{car.drivetrain}</p>
          )}
          {/* Link to this car's listing on the manufacturer's own site.
              Only rendered when the parser supplied a public URL. */}
          {car.source_url && (
            <a
              href={car.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-blue-600 hover:underline"
            >
              View listing on {sourceHost(car.source_url)} ↗
            </a>
          )}
        </div>
        <div className="text-right">
          {car.price_retail_gbp != null && (
            <p className="text-3xl font-bold">
              £{car.price_retail_gbp.toLocaleString("en-GB")}
            </p>
          )}
          {car.mileage_distance != null && (
            <p className="text-gray-600 text-sm">
              {car.mileage_distance.toLocaleString("en-GB")} {car.mileage_metric ?? "mi"}
            </p>
          )}
          {car.battery_soh_value != null && (
            <p className="text-green-700 font-semibold text-sm">
              Battery SoH: {car.battery_soh_value.toFixed(1)}%
            </p>
          )}
        </div>
      </div>

      {/* Photos */}
      {car.photos.length > 0 && (
        <section className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Photos</h2>
          <PhotoGallery photos={car.photos} />
        </section>
      )}

      {/* Specification */}
      <section className="mt-6">
        <h2 className="text-lg font-semibold mb-2">Specification</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <SpecRow label="Model year" value={car.model_year} />
          <SpecRow label="VIN" value={car.vin} />
          <SpecRow label="Reg" value={car.registration_number} />
          <SpecRow
            label="Colour"
            value={car.exterior_label ?? car.exterior_code}
            canonicalLabels={car.capability_labels?.exterior ?? []}
          />
          <SpecRow
            label="Interior"
            value={car.interior_label ?? car.interior_code}
            canonicalLabels={car.capability_labels?.interior ?? []}
          />
          <SpecRow
            label="Wheels"
            value={car.wheels_label ?? car.wheels_code}
            canonicalLabels={car.capability_labels?.wheels ?? []}
          />
          <SpecRow label="Pilot pack" value={car.pilot_pack_label ?? car.pilot_pack_code} />
          <SpecRow label="Plus pack" value={car.plus_pack_label ?? car.plus_pack_code} />
          <SpecRow
            label="Performance pack"
            value={car.performance_pack_label ?? car.performance_pack_code}
          />
          <SpecRow label="Pro pack" value={car.pro_pack_label ?? car.pro_pack_code} />
          <SpecRow
            label="Plus Pro pack"
            value={car.plus_pro_pack_label ?? car.plus_pro_pack_code}
          />
          <SpecRow
            label="Climate pack"
            value={car.climate_pack_label ?? car.climate_pack_code}
          />
          <SpecRow label="Seats" value={car.seats} />
          <SpecRow label="Previous owners" value={car.previous_owners} />
          <SpecRow label="Battery" value={car.battery_kwh != null ? `${car.battery_kwh} kWh` : null} />
          <SpecRow label="Range" value={car.nominal_range_miles != null ? `${car.nominal_range_miles} mi` : null} />
          <SpecRow label="Acceleration" value={car.acceleration_label} />
          <SpecRow label="Handover" value={car.handover_name ?? car.handover_city} />
          <SpecRow
            label="Warranty until"
            value={car.warranty_until ? fmt(car.warranty_until) : null}
          />
          <SpecRow label="First seen" value={fmt(car.first_seen_at)} />
          <SpecRow label="Last seen" value={fmt(car.last_seen_at)} />
        </dl>
        {car.external_notes && (
          <p className="mt-3 text-sm text-gray-600 bg-gray-50 border rounded p-3">
            {car.external_notes}
          </p>
        )}
      </section>

      {/* Manufacture — derived, never stored. Hidden entirely unless the brand
          supplies a build week (Polestar only today), because without one
          every row in it collapses to nothing. */}
      <ManufactureSection car={car} />

      {/* Price breakdown (feature-price-breakdown.md §4.4) */}
      <PriceBreakdown carId={car.car_id} />

      {/* Event timeline */}
      <section className="mt-6">
        <h2 className="text-lg font-semibold mb-2">History</h2>
        {historyEvents.length === 0 ? (
          <p className="text-gray-400 text-sm">No events recorded.</p>
        ) : (
          <EventTable events={historyEvents} />
        )}
      </section>

      {/* DVLA events (M13.B). Distinct from the diff-engine timeline above —
          these rows track DVLA-derived state transitions (tax renewal,
          export). */}
      <section className="mt-6">
        <h2 className="text-lg font-semibold mb-2">DVLA events</h2>
        {car.dvla_events.length === 0 ? (
          <p className="text-gray-400 text-sm">No DVLA events recorded.</p>
        ) : (
          <DvlaEventTable events={car.dvla_events} />
        )}
      </section>

    </div>
  );
}
